"use client";

import { retryWithProjectCacheEviction } from "@/lib/aicheck/cache-eviction";
import { syncProjectCacheInventory } from "@/lib/aicheck/cache-inventory";
import {
  getMergeCheckResultCacheKey,
  MERGE_CHECK_RESULT_CACHE_VERSION,
} from "@/lib/aicheck/cache-keys";
import { projectDataComparisonFromSources } from "@/lib/aicheck/project-data-comparison";
import type {
  AICheckRunResult,
  MergeCheckCacheMetadata,
  MergeCheckStageKey,
  OutputLanguage,
  ProjectDataComparisonSnapshot,
  ProjectDataSourceDescriptor,
} from "@/types/aicheck";

export interface MergeCheckResultCacheEntry {
  mergeCacheKey: string;
  metadata: MergeCheckCacheMetadata;
  result: AICheckRunResult;
  comparison?: ProjectDataComparisonSnapshot;
  cachedAt: string;
}

interface StoredMergeCheckResultCacheEntry {
  v: number;
  data: MergeCheckResultCacheEntry;
}

function fnv32(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function sourceIdentity(source: ProjectDataSourceDescriptor): string {
  return [
    source.kind,
    source.origin?.cacheKey ?? "",
    source.dataDigest,
    source.sourceId,
  ].join(":");
}

export function buildMergeCheckCacheKey(params: {
  baseSource: ProjectDataSourceDescriptor;
  candidateSource: ProjectDataSourceDescriptor;
  language: OutputLanguage;
}): string {
  const raw = [
    "merge-check",
    params.language,
    sourceIdentity(params.baseSource),
    sourceIdentity(params.candidateSource),
  ].join("|");
  return `merge:${fnv32(raw)}`;
}

export function buildMergeStageCheckCacheKey(
  rootMergeCacheKey: string,
  stage: MergeCheckStageKey,
): string {
  return `${rootMergeCacheKey}:stage:${stage}`;
}

export function readMergeCheckResultCache(
  mergeCacheKey: string | null | undefined,
): MergeCheckResultCacheEntry | null {
  if (!mergeCacheKey) return null;
  try {
    const raw = localStorage.getItem(
      getMergeCheckResultCacheKey(mergeCacheKey),
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredMergeCheckResultCacheEntry;
    if (
      parsed?.v !== MERGE_CHECK_RESULT_CACHE_VERSION ||
      parsed.data?.mergeCacheKey !== mergeCacheKey ||
      !parsed.data?.result
    ) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function writeMergeCheckResultCache(params: {
  metadata: MergeCheckCacheMetadata;
  result: AICheckRunResult;
}): void {
  const entry: MergeCheckResultCacheEntry = {
    mergeCacheKey: params.metadata.mergeCacheKey,
    metadata: params.metadata,
    result: params.result,
    comparison:
      projectDataComparisonFromSources({ result: params.result }) ?? undefined,
    cachedAt: new Date().toISOString(),
  };
  try {
    retryWithProjectCacheEviction(
      () => {
        localStorage.setItem(
          getMergeCheckResultCacheKey(entry.mergeCacheKey),
          JSON.stringify({
            v: MERGE_CHECK_RESULT_CACHE_VERSION,
            data: entry,
          } satisfies StoredMergeCheckResultCacheEntry),
        );
        syncProjectCacheInventory();
      },
      { protectedProjectId: params.result.projectId },
    );
  } catch (error) {
    console.warn("[cache:merge-check] Failed to save merge result", error);
  }
}
